import { Link } from 'react-scroll';
import { memo, useState, useCallback } from 'react';

import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import ListSubheader from '@mui/material/ListSubheader';

import NavList from './nav-list';
import { useNavScroll } from '../hooks/useNavScroll';

import type { NavProps, NavGroupProps } from '../types';

// ----------------------------------------------------------------------

function NavSectionVertical({ data, slotProps, ...other }: NavProps) {
  const [activeSection, setActiveSection] = useState(window.location.hash.slice(1));

  return (
    <Stack component="nav" id="nav-section-vertical" {...other}>
      {data.map((group, index) => (
        <Group
          key={group.subheader || index}
          subheader={group.subheader}
          items={group.items}
          slotProps={{ ...slotProps, activeSection }}
          onSetActive={setActiveSection}
        />
      ))}
    </Stack>
  );
}
export default memo(NavSectionVertical);

// ----------------------------------------------------------------------

function Group({
  subheader,
  items,
  slotProps,
  onSetActive,
}: NavGroupProps & { onSetActive: (id: string) => void }) {
  const [open, setOpen] = useState(true);

  const { disableSpy, handleClick, handleSetActive } = useNavScroll(onSetActive);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const renderContent = items.map((list) => (
    <Link
      key={list.title}
      to={list.path}
      data-testid={`nav-${list.path}`}
      spy={!disableSpy}
      offset={-80}
      smooth
      duration={550}
      onClick={() => handleClick(list.path)}
      onSetActive={() => handleSetActive(list.path)}
    >
      <NavList data={list} depth={1} slotProps={slotProps} />
    </Link>
  ));

  return (
    <Stack sx={{ px: 2 }}>
      {subheader ? (
        <>
          <ListSubheader
            disableGutters
            disableSticky
            onClick={handleToggle}
            sx={{
              fontSize: 11,
              cursor: 'pointer',
              typography: 'overline',
              display: 'inline-flex',
              color: 'text.disabled',
              mb: `${slotProps?.gap || 4}px`,
              p: (theme) => theme.spacing(2, 1, 1, 1.5),
              transition: (theme) =>
                theme.transitions.create(['color'], {
                  duration: theme.transitions.duration.shortest,
                }),
              '&:hover': {
                color: 'text.primary',
              },
              ...slotProps?.subheader,
            }}
          >
            {subheader}
          </ListSubheader>

          <Collapse in={open}>{renderContent}</Collapse>
        </>
      ) : (
        renderContent
      )}
    </Stack>
  );
}
